import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Package, Palette, Wand2, ChevronRight, AlertTriangle, CheckCircle2, TrendingUp, Image, Sparkles, Share2 } from 'lucide-react';
import api from '../lib/api';
import { TopBar } from '../components/TopBar';
import { BottomNav } from '../components/BottomNav';
import { useAuth } from '../contexts/AuthContext';


export const DashboardScreen = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [dashRes, sumRes] = await Promise.allSettled([
        api.get('/dashboard'),
        api.get('/dashboard/summary'),
      ]);
      if (dashRes.status === 'fulfilled') setData(dashRes.value.data.dashboard);
      if (sumRes.status === 'fulfilled') setSummary(sumRes.value.data);
    } catch {}
    setLoading(false);
  };

  const d = data || {};
  const s = summary || {};
  const stats = s.stats || {};
  const health = s.listing_health || {};
  const actions = s.priority_actions || [];

  const ACTION_ICON = { sparkles: Sparkles, alert: AlertTriangle, image: Image, check: CheckCircle2, trending: TrendingUp };

  const handleAction = (a) => {
    if (a.action === 'generate_listing') navigate(`/content-studio/${a.product_id}`);
    else if (a.action === 'edit_product') navigate(`/product-edit/${a.product_id}`);
    else if (a.action === 'add_images') navigate(`/product-edit/${a.product_id}`);
    else if (a.action === 'export') navigate(`/preview/${a.product_id}`);
    else if (a.action === 'improve_listing') navigate(`/product-edit/${a.product_id}`);
    else navigate(`/content-studio/${a.product_id}`);
  };

  return (
    <div className="min-h-screen bg-[#F0F2F5] pb-20">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <TopBar
          title="SellGen AI"
          action={<button onClick={logout} className="text-sm px-3 py-1.5 rounded-lg bg-white/20 font-medium" data-testid="logout-button">Logout</button>}
        />
        <div className="p-4 space-y-4">
          {/* Welcome */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-[#128C7E] to-[#075E54] rounded-2xl p-5 text-white" data-testid="welcome-card">
            <p className="text-sm opacity-80">What should you do next?</p>
            <h2 className="text-xl font-bold mt-1">Let's grow your store</h2>
            {health.average_score > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white/80 rounded-full" style={{ width: `${health.average_score}%` }} />
                </div>
                <span className="text-xs font-bold">{health.average_score}%</span>
              </div>
            )}
          </motion.div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Add Product', icon: Plus, route: '/upload', color: 'bg-[#25D366]' },
              { label: 'My Products', icon: Package, route: '/my-products', color: 'bg-[#128C7E]' },
              { label: 'Brand Brain', icon: Palette, route: '/brand-brain', color: d.brand_configured ? 'bg-emerald-600' : 'bg-amber-500' },
              { label: 'Marketing Kit', icon: Wand2, route: '/my-products', color: 'bg-purple-600' },
            ].map((a, i) => (
              <motion.button key={a.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                onClick={() => navigate(a.route)}
                className={`${a.color} text-white rounded-xl p-4 text-left active:scale-95 transition-transform`}
                data-testid={`quick-${a.label.toLowerCase().replace(/\s/g, '-')}`}>
                <a.icon size={22} className="mb-2" />
                <span className="text-sm font-bold block">{a.label}</span>
              </motion.button>
            ))}
          </div>

          {/* Stats */}
          {!loading && (
            <div className="grid grid-cols-4 gap-2">
              {[
                { label: 'Total', value: stats.total || d.total_products || 0, color: 'text-[#128C7E]' },
                { label: 'Draft', value: stats.drafts || 0, color: 'text-slate-500' },
                { label: 'Ready', value: stats.ready_to_publish || 0, color: 'text-amber-600' },
                { label: 'Live', value: stats.published || d.published || 0, color: 'text-green-600' },
              ].map(s => (
                <div key={s.label} className="bg-gray-50 rounded-xl p-2.5 text-center">
                  <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-[9px] text-gray-500">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Priority Actions */}
          {actions.length > 0 && (
            <div className="space-y-2" data-testid="priority-actions">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wide">What to do next</h3>
              {actions.map((a, i) => {
                const Icon = ACTION_ICON[a.icon] || AlertTriangle;
                const colors = a.priority <= 2 ? 'bg-amber-50 border-amber-200' : a.type === 'publish' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200';
                const iconColor = a.priority <= 2 ? 'text-amber-500' : a.type === 'publish' ? 'text-green-500' : 'text-blue-500';
                return (
                  <motion.button key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                    onClick={() => handleAction(a)}
                    className={`w-full flex items-center gap-3 ${colors} border rounded-xl p-3 text-left active:scale-[0.98] transition-transform`}
                    data-testid={`action-${i}`}>
                    {a.product_image && (
                      <div className="w-10 h-10 rounded-lg bg-white overflow-hidden flex-shrink-0 border border-gray-200">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.product_image}`} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700 truncate">{a.product_name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Icon size={11} className={iconColor} />
                        <p className="text-[10px] text-slate-500">{a.message}</p>
                      </div>
                    </div>
                    <span className="text-[10px] text-purple-600 font-bold flex-shrink-0">Fix</span>
                  </motion.button>
                );
              })}
            </div>
          )}

          {/* Listing Health */}
          {!loading && stats.total > 0 && (
            <div className="bg-gradient-to-r from-slate-50 to-purple-50 rounded-xl p-3 border border-slate-200" data-testid="listing-health">
              <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2">Listing Health</h3>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className={`text-lg font-black ${health.average_score >= 70 ? 'text-green-600' : 'text-amber-600'}`}>{health.average_score}%</p>
                  <p className="text-[9px] text-slate-400">Avg Score</p>
                </div>
                <div>
                  <p className="text-lg font-black text-amber-600">{health.needs_attention}</p>
                  <p className="text-[9px] text-slate-400">Need Work</p>
                </div>
                <div>
                  <p className="text-lg font-black text-green-600">{health.marketplace_ready}</p>
                  <p className="text-[9px] text-slate-400">Ready</p>
                </div>
              </div>
            </div>
          )}

          {/* Recent Products with Listing Health */}
          {d.recent_products && d.recent_products.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-slate-700 mb-2">Your Products</h3>
              <div className="space-y-2">
                {d.recent_products.map(p => {
                  const score = p.completion_score || 0;
                  const label = score >= 80 ? 'Strong' : score >= 60 ? 'Needs Improvement' : 'Needs Attention';
                  const labelColor = score >= 80 ? 'text-green-600' : score >= 60 ? 'text-amber-600' : 'text-red-500';
                  const barColor = score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-amber-500' : 'bg-red-400';
                  // Find the most important marketplace status from priority actions
                  const matchingAction = actions.find(a => a.product_id === p.id);
                  return (
                    <button key={p.id} onClick={() => navigate(`/product-edit/${p.id}`)}
                      className="w-full bg-white border border-gray-200 rounded-xl p-3 text-left active:scale-[0.98] transition-transform"
                      data-testid={`recent-${p.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                          {p.images?.[0] ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.images[0].path}`} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><Package size={20} className="text-gray-300" /></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{p.name || 'Untitled'}</p>
                          {p.price > 0 && <span className="text-[10px] text-slate-500">₹{p.price}</span>}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className={`text-sm font-bold ${labelColor}`}>{score}</span>
                          <p className={`text-[9px] font-medium ${labelColor}`}>{label}</p>
                        </div>
                      </div>
                      {/* Score bar */}
                      <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${score}%` }} />
                      </div>
                      {/* Marketplace status */}
                      {matchingAction && (
                        <div className="mt-1.5 flex items-center gap-1">
                          {matchingAction.priority <= 2 ? <AlertTriangle size={10} className="text-amber-500" /> : <CheckCircle2 size={10} className="text-green-500" />}
                          <span className="text-[9px] text-slate-500">{matchingAction.message}</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Brand Brain Setup */}
          {!loading && !d.brand_configured && (
            <button onClick={() => navigate('/brand-brain')}
              className="w-full bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 text-left active:scale-[0.98] transition-transform"
              data-testid="setup-brand-brain">
              <div className="flex items-center gap-3">
                <Palette size={24} className="text-amber-600" />
                <div>
                  <p className="text-sm font-bold text-amber-800">Setup Brand Brain</p>
                  <p className="text-xs text-amber-600 mt-0.5">Personalize AI content with your brand identity</p>
                </div>
              </div>
            </button>
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
};
